// #540: the two candidate sets of today's draft, with gear and bodyweight
// only, for the draft's own toggle. One rule for the three doors (the Home
// program card, Progress, the Create hub), at one cap for both sets.
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { Exercise } from "./schemas/exercise.js";
import { TODAY_CANDIDATES_PER_MUSCLE, type TodayGap } from "./todaySessionProposal.js";
import { proposeForVolumeGapsScoped, type VolumeGapProposal } from "./volumeGapProposal.js";

export type TodayGapSets = {
  gaps: TodayGap[];
  /** The same gaps, bodyweight exercises only. A gap with none is out. */
  bodyweightGaps: TodayGap[];
  /** The MEV muscles gear trains and no bodyweight exercise does. */
  noBodyweight: CatalogMuscleSlug[];
};

/** Each gap with its proposal's candidates; a gap left with none is out. */
const withCandidates = (
  gaps: ReadonlyArray<TodayGap>,
  proposals: ReadonlyArray<VolumeGapProposal>,
): TodayGap[] =>
  gaps.flatMap((g, i) => {
    const found = proposals[i]?.candidates ?? [];
    const candidates = found.map((c) => ({ slug: c.slug, setsToClose: c.setsToClose, weight: c.weight }));
    return candidates.length > 0 ? [{ ...g, candidates }] : [];
  });

/** The gaps' candidates read again from the catalog, both ways
 *  (proposeForVolumeGapsScoped), on what `isAvailable` lets through. */
export const todayGapSets = (
  gaps: ReadonlyArray<TodayGap>,
  catalog: ReadonlyArray<Exercise>,
  isAvailable?: (ex: Exercise) => boolean,
): TodayGapSets => {
  const base = {
    gaps: gaps.map((g) => ({ muscle: g.muscle, weeklySets: g.weekSets ?? 0, gap: g.gap })),
    catalog,
    maxPerMuscle: TODAY_CANDIDATES_PER_MUSCLE,
    ...(isAvailable ? { isAvailable } : {}),
  };
  const gear = proposeForVolumeGapsScoped(base);
  const bodyweight = proposeForVolumeGapsScoped({ ...base, bodyweightOnly: true });
  // A MAV gap only fills spare time: its muscle is never named.
  const mev = new Set(gaps.filter((g) => g.target !== "mav").map((g) => g.muscle));
  return {
    gaps: withCandidates(gaps, gear.proposals),
    bodyweightGaps: withCandidates(gaps, bodyweight.proposals),
    noBodyweight: bodyweight.noBodyweight.filter((m) => mev.has(m)),
  };
};
